<template>
  <article>
    <div id="CONTENT_BUNDLES">
      <section class="container-fixed offset-bottom-4">
        <form>
          <h3 class="offset-bottom-2">{{ $t('bundles.headerFilter') }}</h3>
          <div class="row offset-top-2">
            <div class="col-l-3 col-m-6 col-s-12" :title="$t('bundles.tooltip_filterFrom')">
              <label class="align-with-buttons">
                <span class="form-label">{{ $t('common.periodFrom') }}</span>
                <VDatepicker v-model="periodFrom" :disabled-date="disabledDate" input-class="period-from" />
              </label>
            </div>
            <div class="col-l-3 col-m-6 col-s-12">
              <label class="align-with-buttons" :title="$t('bundles.tooltip_filterTo')">
                <span class="form-label">{{ $t('common.periodUntil') }}</span>
                <VDatepicker v-model="periodUntil" :disabled-date="disabledDate" input-class="period-until" />
              </label>
            </div>
            <div class="col-l-6 col-m-12">
              <button
                :disabled="searchform.isSearchLoading"
                type="submit"
                :title="$t('bundles.tooltip_filterConfirmSearch')"
                class="btn btn-default pull-left offset-bottom-1"
                style="margin-right: 24px"
                @click.prevent="findRange"
                >{{ $t('common.btnSearchPeriod') }}
              </button>

              <div class="btn-sectioned pull-left">
                <button
                  :disabled="searchform.isSearchLoading"
                  type="button"
                  :title="$t('bundles.tooltip_filterToday')"
                  class="btn btn-default"
                  @click="findToday"
                >
                  {{ $t('common.btnSearchToday') }}
                </button>
                <button
                  :disabled="searchform.isSearchLoading"
                  type="button"
                  :title="$t('bundles.tooltip_filterYesterday')"
                  class="btn btn-default"
                  @click="findYesterday"
                  >{{ $t('common.btnSearchYesterday') }}
                </button>
                <button
                  :disabled="searchform.isSearchLoading"
                  type="button"
                  :title="$t('bundles.tooltip_filterLastWeek')"
                  class="btn btn-default"
                  @click="findLastWeek"
                  >{{ $t('common.btnSearchLastWeek') }}
                </button>
              </div>
            </div>
          </div>

          <VSearchFormStatus v-bind="searchform" spinner closable @close="searchReset" />
        </form>
      </section>

      <template v-if="validRows">
        <section class="container-fluid grayed-offset">
          <div class="container-fixed">
            <h3 id="CONTENT_BUNDLES_OVERVIEW">{{ $t('bundles.overviewHeader') }}</h3>
            <p />
            <transition name="fade" mode="out-in" appear>
              <VueGoodTable v-bind="table">
                <template #table-row-after="{ row, index }">
                  <td>
                    <button class="btn btn-default btn-small" @click.stop="selectRow(row, index)">
                      {{ $t('bundles.btnDetails') }}
                    </button>
                  </td>
                </template>
                <template #table-column="{ column }">
                  <VTableHeader :columns="table.columns" :column="column" locale-namespace="bundles" />
                </template>
                <div slot="emptystate" class="center-align text-disabled">
                  {{ $t('common.errorNoData') }}
                </div>
              </VueGoodTable>
            </transition>
          </div>
        </section>

        <transition name="fade" mode="out-in" appear>
          <div v-if="details" id="CONTENT_BUNDLES_OVERVIEW_DETAILS" :key="details.templateId" class="container-fixed offset-bottom-4">
            <VSection level="3" label="bundles.informationHeader">
              <VDescriptionListDivided>
                <VEntry :data="details" field="templateId" name="bundles.bundleTemplateId" />
                <VEntry :data="details" field="templateDescription" name="bundles.description" />
                <VEntry :data="details" field="instanceUsableFrom" name="bundles.bookableFrom" />
                <VEntry :data="details" field="instanceUsableTo" name="bundles.bookableTo" />
                <VEntry :data="details" field="purchaseIdentifierTypeName" name="bundles.purchaseVia" />
                <VEntry :data="details" field="purchaseIdentifierName" name="bundles.vasCode" />
                <VEntry :data="details" field="partnerContract" name="bundles.partnerContractNo" />
                <VEntry :data="details" field="businessPartnerName" name="bundles.businessPartnerNo" />
              </VDescriptionListDivided>
            </VSection>

            <VSection level="3" label="bundles.instanceHeader">
              <VDescriptionListDivided>
                <VEntry :data="details" field="instanceId" name="bundles.instanceId" />
                <VEntry :data="details" field="instanceStateDescription" name="bundles.state" />
                <VEntry :data="details" field="instanceCreationDate" name="bundles.creationDate" />
                <VEntry :data="details" field="instanceUsableFrom" name="bundles.usableFrom" />
                <VEntry :data="details" field="instanceUsableTo" name="bundles.usableTo" />
                <VEntry :data="details" field="templateId" name="bundles.subscriptionTemplateId" />
                <VEntry :data="details" field="templateDescription" name="bundles.subscriptionTemplateDescription" />
              </VDescriptionListDivided>
            </VSection>

            <VSection level="3" label="bundles.positionsHeader">
              <VueGoodTable
                style-class="table table-hover table-striped"
                :columns="positions.columns"
                :rows="calaculateUniqueBundlePositionsFields()"
                :paginate="shouldShowTablePageControlsByMinElements(positions, 10)"
                :rows-per-page-text="$t('common.tablePerPage')"
                :of-text="$t('common.tableOf')"
                :all-text="$t('common.tableAll')"
                :prev-text="$t('common.tablePrev')"
                :next-text="$t('common.tableNext')"
                :line-numbers="false"
              >
                <template #table-column="{ column }">
                  <VTableHeader :columns="positions.columns" :column="column" locale-namespace="bundles" />
                </template>
              </VueGoodTable>
            </VSection>
          </div>
        </transition>
      </template>
    </div>
  </article>
</template>

<script>
import format from 'date-fns/format';
import parse from 'date-fns/parse';
import subDays from 'date-fns/subDays';
import addDays from 'date-fns/addDays';
import isValid from 'date-fns/isValid';
import VDatepicker from '@/components/Form/DatePicker';
import Proxy from '@/proxies/TransactionBundlesProxy';
import VSection from '@/components/Section';
import VDescriptionListDivided from '@/components/DescriptionList/Divided';
import VEntry from '@/components/DescriptionList/Entry';
import TitleMixin from '@/mixins/title';
import SearchFormMixin from '@/mixins/searchform';
import TableMixin from '@/mixins/table';
import TableUtilitiesMixin from '@/mixins/tableUtilities';
import { dateTimeTransferFormat, dateUrlFormat, formatDateTime } from '@/utils/date';
import { CONTENT_BUNDLES, CONTENT_BUNDLES_OVERVIEW, CONTENT_BUNDLES_OVERVIEW_DETAILS } from '@route-types';
import overviewColumns from './overviewColumns';
import positionsColumns from './positionsColumns';

export default {
  name: 'ContentBundles',

  components: {
    VDatepicker,
    VueGoodTable: () => import('vue-good-table').then(m => m.VueGoodTable),
    VSection,
    VDescriptionListDivided,
    VEntry,
    VSearchFormStatus: () => import('@/components/SearchFormStatus'),
    VTableHeader: () => import('@/components/Table/Header'),
  },

  mixins: [TitleMixin, SearchFormMixin, TableMixin, TableUtilitiesMixin],

  data() {
    return {
      periodFrom: new Date(),
      periodUntil: new Date(),
      details: null,
      table: {
        columns: overviewColumns,
        rows: [],
      },
      positions: {
        columns: positionsColumns,
      },
    };
  },

  computed: {
    validRows() {
      return this.table.rows && this.table.rows.length;
    },

    routeParams() {
      return this.$route.params;
    },
  },

  watch: {
    routeParams: {
      handler: 'onParamsChanged',
      deep: true,
    },
    periodFrom: 'adjustPeriodUntil',
    periodUntil: 'adjustPeriodFrom',
  },

  created() {
    this.$webtrekk.viewPage('contentbundles', 'basket');
  },

  mounted() {
    // restore from route:
    if (!this.parseDates(this.routeParams)) {
      // use replace instead of push so browser back won't get stuck here
      this.saveRangeRoute('replace');
    } else {
      this.find();
    }
  },

  methods: {
    adjustPeriodUntil() {
      if (!this.isRangeValid()) {
        this.periodUntil = addDays(this.periodFrom, 1);
        this.$nextTick(() => {
          this.searchWarningMessage('Invalid Range');
        });
      } else {
        this.searchReset();
      }
    },

    adjustPeriodFrom() {
      if (!this.isRangeValid()) {
        this.periodFrom = this.periodUntil;
        this.$nextTick(() => {
          this.searchWarningMessage('Invalid Range');
        });
      } else {
        this.searchReset();
      }
    },

    onParamsChanged(val, oldVal) {
      if (!val.from) {
        this.$emit('reset');
        this.periodFrom = new Date();
        this.periodUntil = new Date();
        this.saveRangeRoute('replace');
      } else if ((val && !oldVal) || val.from !== oldVal.from || val.to !== oldVal.to) {
        if (this.parseDates(val)) this.find();
      }
    },

    saveRangeRoute(method) {
      if (!method) method = 'push';
      const from = format(this.periodFrom, dateUrlFormat);
      const to = format(this.periodUntil, dateUrlFormat);
      this.$router[method]({ name: CONTENT_BUNDLES_OVERVIEW, params: { from, to } });
    },

    clearRangeRoute() {
      this.$router.push({ name: CONTENT_BUNDLES, params: {} });
    },

    clearRangeAndTable() {
      this.table.rows = null; // clear list
      this.clearRangeRoute();
    },

    parseDates(range) {
      const periodFrom = parse(range.from, dateUrlFormat, new Date());
      const periodUntil = parse(range.to, dateUrlFormat, new Date());
      if (!isValid(periodFrom)) return false;
      if (!isValid(periodUntil)) return false;
      this.periodFrom = periodFrom;
      this.periodUntil = periodUntil;
      return this.isRangeValid();
    },

    isRangeValid() {
      if (this.periodFrom === null) {
        this.searchErrorMessage(this.$t('common.errInvalidPeriodFrom'));
        return false;
      }
      if (this.periodUntil === null) {
        this.searchErrorMessage(this.$t('common.errInvalidPeriodUntil'));
        return false;
      }

      if (this.periodFrom > this.periodUntil) {
        this.searchErrorMessage(this.$t('common.errPeriodFromBeforePeriodUntil'));
        return false;
      }
      return true;
    },

    findRange() {
      if (this.periodFrom === null) {
        this.searchErrorMessage(this.$t('common.errInvalidPeriodFrom'));
        this.reset();
        return;
      }
      if (this.periodUntil === null) {
        this.searchErrorMessage(this.$t('common.errInvalidPeriodUntil'));
        this.reset();
        return;
      }

      if (this.periodFrom > this.periodUntil) {
        this.searchErrorMessage(this.$t('common.errPeriodFromBeforePeriodUntil'));
        this.clearRangeAndTable();
        return;
      }

      this.saveRangeRoute();
      this.find();
    },

    findToday() {
      this.periodFrom = new Date();
      this.periodUntil = new Date();
      this.saveRangeRoute();
      this.find();
    },

    findYesterday() {
      this.periodFrom = subDays(new Date(), 1);
      this.periodUntil = subDays(new Date(), 1);
      this.saveRangeRoute();
      this.find();
    },

    findLastWeek() {
      this.periodFrom = subDays(new Date(), 7);
      this.periodUntil = new Date();
      this.saveRangeRoute();
      this.find();
    },

    async find() {
      this.searchLoading();
      this.details = null;
      try {
        const requestPeriodFrom = this.periodFrom;
        const requestPeriodUntil = this.periodUntil;
        /* const requestLocale = this.$i18n.locale;
         set start time on 00:00:00
         request periodFrom and periodUntil:
         used as direct input for database sql between search */
        requestPeriodFrom.setHours(0);
        requestPeriodFrom.setMinutes(0);
        requestPeriodFrom.setSeconds(0);
        requestPeriodFrom.setMilliseconds(0);

        requestPeriodUntil.setHours(23);
        requestPeriodUntil.setMinutes(59);
        requestPeriodUntil.setSeconds(59);
        requestPeriodUntil.setMilliseconds(999);

        const list = await new Proxy({
          periodFrom: format(requestPeriodFrom, dateTimeTransferFormat),
          periodUntil: format(requestPeriodUntil, dateTimeTransferFormat),
        }).all();

        if (list.length === 0) {
          this.searchErrorNoData();
          this.table.rows = null;
        } else {
          this.searchSuccess();
          this.table.rows = this.translateRows(list);

          // restore from route:
          const instanceId = parseInt(this.routeParams.instanceId, 10);
          if (!Number.isNaN(instanceId)) {
            this.details = list.find(row => row.instanceId === instanceId);
          }

          //  this.addFilterDropDownListByField(this.table, 'templateId');
          this.addFilterDropDownListByField(this.table, 'instanceStateDescription');
          this.handleDefaultSearchFields(this.table);
          this.handleTablePageControlsByMinElements(this.table);
        }
      } catch (error) {
        this.table.rows = null;
        this.searchErrorMessage(this.$t('common.errorDefault'));
      }
    },

    translateRows(rows) {
      if (rows) {
        rows.map(row => {
          row.instanceCreationDate = formatDateTime(row.instanceCreationDate);
          row.instanceUsableFrom = formatDateTime(row.instanceUsableFrom);
          row.instanceUsableTo = formatDateTime(row.instanceUsableTo);
          row.templateVersionValidFrom = formatDateTime(row.templateVersionValidFrom);
          row.templateVersionValidTo = formatDateTime(row.templateVersionValidTo);
          row = this.translateDetails(row);
          return row;
        });
      }
      return rows;
    },

    translateDetails(details) {
      if (details && details.instancePositions) {
        details.instancePositions.map(detail => {
          detail.lastUsed = formatDateTime(detail.lastUsed);
          return detail;
        });
      }
      return details;
    },

    selectRow(row, index) {
      this.setAriaSelected(index);

      this.details = this.table.rows[row.originalIndex];

      // save route:
      const from = format(this.periodFrom, dateUrlFormat);
      const to = format(this.periodUntil, dateUrlFormat);
      const { instanceId } = this.details;
      this.$router.push({
        name: CONTENT_BUNDLES_OVERVIEW_DETAILS,
        params: { from, to, instanceId },
      });

      // fill unique find combo-boxes from positionColumns.js
      this.positions.rows = this.calaculateUniqueBundlePositionsFields();
      this.addFilterDropDownListByField(this.positions, 'templatePositionTypeDescription');
      this.addFilterDropDownListByField(this.positions, 'vasTypeDescription');
      this.handleDefaultSearchFields(this.positions);
      this.handleTablePageControlsByMinElements(this.positions, 10);
    },

    tableRowStyle(row) {
      if (this.details !== null && row.instanceId === this.details.instanceId) {
        return 'brand';
      }
      return '';
    },

    /* for later use of factoring bundles
      getDetailsCurrency() {
      if (this.details !== null
        && this.details.currency !== null) {
        return this.details.currency;
      }
      return 'EUR';
      },
      */
    calculateResellingRemainingVolume(resellingIncludedVolume, resellingConsumedVolume) {
      if (resellingIncludedVolume === null || resellingConsumedVolume === null) {
        return '';
      }
      if (resellingIncludedVolume >= resellingConsumedVolume) {
        return (resellingIncludedVolume - resellingConsumedVolume).toString();
      }

      return '0';
    },

    calaculateUniqueBundlePositionsFields() {
      if (
        this.details === null ||
        this.details.instancePositions === undefined ||
        this.details.instancePositions === null ||
        this.details.instancePositions.length === 0
      ) {
        return [];
      }

      this.details.instancePositions.forEach(element => {
        // default settings [undefined check to prevent blinking text on computed cycle]
        if (element.calcUniqueIncludedVolume === undefined) {
          element.calcUniqueIncludedVolume = '';
        }
        if (element.calcUniqueConsumedVolume === undefined) {
          element.calcUniqueConsumedVolume = '';
        }
        if (element.calcUniqueRemainingVolume === undefined) {
          element.calcUniqueRemainingVolume = '';
        }
        if (element.calcUniqueRemainingToPayVolume === undefined) {
          element.calcUniqueRemainingToPayVolume = '';
        }

        // reselling
        if (element.resellingConsumedVolume !== null) {
          element.calcUniqueConsumedVolume = element.resellingConsumedVolume.toString();
        }
        if (element.resellingIncludedVolume !== null) {
          element.calcUniqueIncludedVolume = element.resellingIncludedVolume.toString();
        }

        if (element.templatePositionType !== null) {
          switch (element.templatePositionType) {
            case 0: // type normal = Consumption of a purchased number
              // reselling - volume in units
              // ===========================
              // calculate reselling remainingVolume
              if (element.resellingIncludedVolume !== null && element.resellingConsumedVolume !== null) {
                element.calcUniqueRemainingVolume = this.calculateResellingRemainingVolume(element.resellingIncludedVolume, element.resellingConsumedVolume);
                // check: element.resellingInitToPayVolume should only be set for type = 1 inverse
              }
              break;
            case 1: // type inverse = Extra service if a number of units/amount is reached
              // reselling - volume in units
              // ===========================
              // calculate reselling remainingVolume
              if (element.resellingIncludedVolume !== null && element.resellingConsumedVolume !== null) {
                element.calcUniqueIncludedVolume = element.resellingIncludedVolume.toString();

                // check: element.resellingInitToPayVolume should only be set for type = 1 inverse
                if (element.resellingInitToPayVolume === null) {
                  element.calcUniqueRemainingVolume = this.calculateResellingRemainingVolume(element.resellingIncludedVolume, element.resellingConsumedVolume);
                } else if (element.resellingInitToPayVolume >= element.resellingConsumedVolume) {
                  element.calcUniqueRemainingVolume = element.resellingIncludedVolume.toString();
                } else if (element.resellingConsumedVolume >= element.resellingInitToPayVolume + element.resellingIncludedVolume) {
                  element.calcUniqueRemainingVolume = (
                    element.resellingConsumedVolume -
                    element.resellingInitToPayVolume -
                    element.resellingIncludedVolume
                  ).toString();
                }
                // calculate remainingToPayVolume
                element.calcUniqueRemainingToPayVolume = (element.resellingInitToPayVolume - element.resellingConsumedVolume).toString();
              }
              break;
            case 2: // type flatrate
              element.calcUniqueRemainingVolume = this.$t('bundles.bundlePositionTypeFlatrate');
              element.calcUniqueRemainingToPayVolume = this.$t('bundles.bundlePositionTypeFlatrate');
              break;
            default:
              break;
          }
        }
      }); // for each
      return this.details.instancePositions;
    },

    disabledDate(date) {
      const maxDate = new Date();
      maxDate.setHours(23, 59, 59, 59);
      return date.getTime() > maxDate.getTime();
    },
  },
};
</script>

<style scoped>
.align-with-buttons {
  position: relative;
  top: -22px;
  width: 100%;
}
</style>
